/**
 * Phone PDP shell behaviour.
 *
 * The gallery (stage + thumbnail row beneath + overlaid dots + enlarge
 * button) is bespoke to this design, not the theme's stock aside-rail
 * gallery, so it gets its own Swiper instance here rather than reusing
 * global.js's sliderInit/subSliderInit — those hard-code a vertical thumb
 * rail at desktop widths, which this layout never wants.
 *
 * The zoom modal (product-media-modal) IS the theme's existing shared
 * component: its own slider comes from global.js's popupSliderInit (wired up
 * by update-slider.js, already loaded by this section), so only the
 * click-to-zoom toggle is added here.
 *
 * Variant selection (steps 1-2) reads/writes the URL and re-fetches this
 * section's markup on change (the same section-rendering pattern global.js's
 * own VariantSelects.renderProductInfo uses) rather than re-implementing the
 * colour filter or price formatting in JS — both are Liquid logic already,
 * and re-rendering guarantees they can't drift from a second copy.
 */
;(function () {
	// Fired whenever anything pay-today depends on changes (plan add/skip,
	// province choice, SIM toggle, device variant swap). One event, one
	// listener (initPayToday) — keeps the four state-owning steps decoupled
	// from the thing that reads their state, per D6.
	const PAY_TODAY_REFRESH = 'speakout:pay-today:refresh'

	function refreshPayToday(root) {
		;(root || document).dispatchEvent(
			new CustomEvent(PAY_TODAY_REFRESH, { bubbles: true })
		)
	}

	function prefersReducedMotion() {
		return (
			window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		)
	}

	// <product-modal> relocates itself to <body> on connect (see
	// fetchAndSwap's own comment on this) — almost certainly before this
	// ever runs, even on the very first page load. Scoping the search to
	// `root` (the section wrapper) therefore never finds it, and no
	// click-to-zoom listener ever gets bound at all — a pre-existing bug,
	// just invisible until the crop-by-default CSS bug was fixed (before
	// that, the image already looked "zoomed" by default, masking the
	// fact that clicking it did nothing). Search the whole document
	// instead; the modal's final location doesn't depend on `root`.
	function initZoomImage(root) {
		const imagesWrapper = document.querySelector('.product-media-modal__content')
		const images = imagesWrapper?.querySelectorAll('.js-image-zoom') || []

		images.forEach((el) => {
			if (el.dataset.speakoutZoomBound) return
			el.dataset.speakoutZoomBound = 'true'
			el.addEventListener('click', () => {
				imagesWrapper.classList.toggle('zoom')
			})
		})
	}

	function initGallery(gallery) {
		if (!gallery || gallery.dataset.speakoutGalleryInit) return
		if (typeof Swiper === 'undefined') return

		const mainEl = gallery.querySelector('.speakout-gallery__main')
		if (!mainEl) return

		gallery.dataset.speakoutGalleryInit = 'true'

		const slides = Array.prototype.slice.call(
			mainEl.querySelectorAll('.speakout-gallery__slide')
		)
		const thumbButtons = Array.prototype.slice.call(
			gallery.querySelectorAll('.speakout-gallery__thumb')
		)
		const enlargeButton = gallery.querySelector(
			'[data-speakout-enlarge-button]'
		)
		const dotsEl = gallery.querySelector('.speakout-gallery__dots')

		function syncActive(index) {
			thumbButtons.forEach((btn, i) => {
				btn.classList.toggle('is-active', i === index)
			})
			if (enlargeButton && slides[index]) {
				enlargeButton.dataset.mediaId = slides[index].dataset.mediaId
			}
			const popup = document.querySelector('.js-popup-slider')
			if (popup && popup.swiper) popup.swiper.slideTo(index)
		}

		const swiper = new Swiper(mainEl, {
			slidesPerView: 1,
			spaceBetween: 0,
			speed: prefersReducedMotion() ? 0 : 250,
			keyboard: { enabled: true },
			pagination: dotsEl ? { el: dotsEl, clickable: true } : false,
			on: {
				slideChange() {
					syncActive(this.activeIndex)
				},
			},
		})

		gallery._speakoutSwiper = swiper

		thumbButtons.forEach((btn, i) => {
			btn.addEventListener('click', () => swiper.slideTo(i))
		})
	}

	function destroyGallery(gallery) {
		if (gallery && gallery._speakoutSwiper) {
			gallery._speakoutSwiper.destroy(true, true)
		}
	}

	class SpeakoutVariantSelects extends HTMLElement {
		constructor() {
			super()
			this.onChange = this.onChange.bind(this)
			this.addEventListener('change', this.onChange)
			this.updateAvailability()
		}

		getVariantData() {
			if (this._variantData) return this._variantData
			const script = this.querySelector('[data-speakout-variant-json]')
			this._variantData = script ? JSON.parse(script.textContent) : []
			return this._variantData
		}

		getSteps() {
			return Array.prototype.slice.call(
				this.querySelectorAll('[data-speakout-variant-step]')
			)
		}

		getSelectedValues() {
			return this.getSteps().map((step) => {
				const checked = step.querySelector('input:checked')
				return checked ? checked.value : null
			})
		}

		// Combination-aware: a value is only enabled if some real, available
		// variant matches it together with whatever the OTHER steps currently
		// have selected — not just "is this value available on any variant".
		updateAvailability() {
			const variants = this.getVariantData()
			const steps = this.getSteps()
			const selected = this.getSelectedValues()

			steps.forEach((step, stepIndex) => {
				const inputs = step.querySelectorAll('input[type="radio"]')
				inputs.forEach((input) => {
					const candidate = selected.slice()
					candidate[stepIndex] = input.value
					const hasAvailable = variants.some(
						(variant) =>
							variant.available &&
							candidate.every(
								(value, i) => value == null || variant.options[i] === value
							)
					)
					input.disabled = !hasAvailable
					input.setAttribute('aria-disabled', String(!hasAvailable))
					const label = input.nextElementSibling
					if (label) label.classList.toggle('is-disabled', !hasAvailable)
				})
			})
		}

		findMatchingVariant() {
			const selected = this.getSelectedValues()
			if (selected.some((value) => value == null)) return null
			return this.getVariantData().find((variant) =>
				selected.every((value, i) => variant.options[i] === value)
			)
		}

		onChange(event) {
			if (event.target.tagName !== 'INPUT') return
			this.updateAvailability()

			const variant = this.findMatchingVariant()
			if (!variant) return

			this.updateURL(variant)
			this.fetchAndSwap(variant)
		}

		updateURL(variant) {
			if (!this.dataset.url) return
			window.history.replaceState(
				{},
				'',
				`${this.dataset.url}?variant=${variant.id}`
			)
		}

		fetchAndSwap(variant) {
			const sectionId = this.dataset.section
			this._pendingVariantId = variant.id

			fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${sectionId}`)
				.then((response) => response.text())
				.then((responseText) => {
					// A slower response for a since-changed selection must not
					// overwrite what the visitor is looking at now.
					if (this._pendingVariantId !== variant.id) return

					const html = new DOMParser().parseFromString(
						responseText,
						'text/html'
					)
					const freshColumn = html.querySelector(
						'.speakout-configurator__gallery-column'
					)
					const currentColumn = document.querySelector(
						`#SpeakoutConfigurator-${sectionId} .speakout-configurator__gallery-column`
					)
					if (!freshColumn || !currentColumn) return

					destroyGallery(currentColumn.querySelector('[data-speakout-gallery]'))

					// <product-modal> relocates itself out to <body> on connect
					// (a portal pattern — escapes this column's own stacking
					// context so the dialog and its backdrop aren't clipped).
					// That happens before any variant change ever runs, so by
					// the time we get here the modal already isn't inside
					// currentColumn — replacing currentColumn's innerHTML never
					// touches it, and initGallery/initZoomImage below then
					// creates and relocates ANOTHER one to <body>, leaving the
					// old (stale colour) copy behind. Two elements, duplicate
					// ID, the zoom popup showing both colours' images at once.
					// Remove whichever one already escaped before swapping.
					const staleModal = document.getElementById(
						`ProductModal-${sectionId}`
					)
					if (staleModal) staleModal.remove()

					currentColumn.innerHTML = freshColumn.innerHTML

					initGallery(currentColumn.querySelector('[data-speakout-gallery]'))
					initZoomImage(
						document.getElementById(`shopify-section-${sectionId}`) ||
							document
					)

					// The zoom modal's own Swiper (global.js's popupSliderInit,
					// wired up by update-slider.js) only ever runs on initial
					// page load and on Shopify's theme-editor shopify:section:load
					// event — neither fires for this swap. Without it the fresh
					// modal's slides never get Swiper's sizing/transform applied,
					// so they render stacked/unsized — looked like the image was
					// stuck already zoomed in, reported live on the storefront.
					if (typeof popupSliderInit === 'function') popupSliderInit(true)

					refreshPayToday(this)
				})
				.catch(() => {
					// The URL and the selected inputs are already correct; only
					// the gallery/price refresh is skipped this one time rather
					// than showing a broken or half-updated state.
				})
		}
	}

	if (!customElements.get('speakout-variant-selects')) {
		customElements.define('speakout-variant-selects', SpeakoutVariantSelects)
	}

	// Step 3+4: plan and province. STATUS WARNING (brief): the province
	// decision is blocked; this is exactly the fallback the spec defines —
	// read a carried province if present, otherwise reveal the control on
	// the first "Add this plan" click, never a guess. The actual atomic
	// add-to-cart is D6's job; this only sets state D6 will read.
	function initPlanStep(root) {
		const step = (root || document).querySelector('[data-speakout-plan-step]')
		if (!step || step.dataset.speakoutPlanStepInit) return
		step.dataset.speakoutPlanStepInit = 'true'

		const addBtn = step.querySelector('[data-plan-action="add"]')
		const skipBtn = step.querySelector('[data-plan-action="skip"]')
		const bodyEl = step.querySelector('.speakout-plan-step__body')
		const fieldEl = step.querySelector('[data-speakout-province-field]')
		const provinceSelect = fieldEl ? fieldEl.querySelector('select') : null
		const provinceError = step.querySelector('[data-province-error]')
		const provinceErrorText = step.querySelector('[data-province-error-text]')
		const swapEl = step.querySelector('[data-speakout-plan-swap]')
		const swapConfirmBtn = step.querySelector('[data-plan-swap-confirm]')

		// Phone PDP plan picker: "See all" opens an in-step dropdown instead of
		// leaving for the plans collection (a phone purchase has no IMEI to
		// check). The chosen option is copied onto the step's data attributes,
		// which collectSelection already reads for pay-today and the atomic add.
		const seeAllBtn = step.querySelector('[data-plan-see-all]')
		const pickerEl = step.querySelector('[data-plan-picker]')
		const pickerSelect = pickerEl ? pickerEl.querySelector('[data-plan-picker-select]') : null
		const planLabelEl = step.querySelector('[data-plan-label]')
		const planBadgeEl = step.querySelector('[data-plan-badge]')
		if (seeAllBtn && pickerEl && pickerSelect) {
			const planRowEl = step.querySelector('.speakout-plan-step__row')
			seeAllBtn.addEventListener('click', (event) => {
				event.preventDefault()
				// The plan box itself becomes the dropdown: one input, never two.
				if (planRowEl) planRowEl.hidden = true
				pickerEl.hidden = false
				seeAllBtn.setAttribute('aria-expanded', 'true')
				pickerSelect.focus()
				// Open the native list straight away where the browser allows it.
				try {
					if (typeof pickerSelect.showPicker === 'function') pickerSelect.showPicker()
				} catch (e) {
					/* not supported or blocked: focused select is enough */
				}
			})
			pickerSelect.addEventListener('change', () => {
				const option = pickerSelect.selectedOptions[0]
				if (!option) return
				step.dataset.planVariantId = option.value
				step.dataset.planPriceCents = option.dataset.planPriceCents || '0'
				step.dataset.planPartnerCode = option.dataset.planPartnerCode || ''
				step.dataset.planBillingLabel = option.dataset.planBillingLabel || ''
				step.dataset.planAvailable = option.dataset.planAvailable || 'true'
				if (planLabelEl) planLabelEl.textContent = option.dataset.planLabel || ''
				if (planBadgeEl) planBadgeEl.hidden = option.dataset.planBestDeal !== 'true'
				// Picking a plan means the customer wants one: if "Just the
				// phone" was selected, switch back through the add button so the
				// province step reveals exactly as it does on a normal add.
				if (step.dataset.planIncluded !== 'true' && addBtn) {
					addBtn.click()
				} else {
					refreshPayToday(step)
				}
			})
		}

		function getCarriedProvinceCode() {
			try {
				return sessionStorage.getItem('speakout.province_code')
			} catch (e) {
				return null
			}
		}

		function setCarriedProvinceCode(code) {
			try {
				sessionStorage.setItem('speakout.province_code', code)
			} catch (e) {
				/* sessionStorage unavailable (private mode, etc.) — the field
				   still reflects the choice for this page view */
			}
		}

		function findOption(code) {
			return provinceSelect
				? provinceSelect.querySelector(`option[value="${CSS.escape(code)}"]`)
				: null
		}

		// Shown present (carried on load): no animation — set both attributes
		// together so nothing paints the collapsed state first.
		function showFieldImmediately() {
			if (!fieldEl) return
			fieldEl.hidden = false
			fieldEl.setAttribute('data-open', '')
		}

		// Revealed by an action (B8): slides open a frame later, then focuses.
		function showFieldAnimated(focusField) {
			if (!fieldEl) return
			fieldEl.hidden = false
			requestAnimationFrame(() => fieldEl.setAttribute('data-open', ''))
			if (focusField && provinceSelect) provinceSelect.focus()
		}

		function hideProvinceUI() {
			if (!fieldEl) return
			fieldEl.hidden = true
			fieldEl.removeAttribute('data-open')
		}

		function showProvinceError(message) {
			if (provinceErrorText) provinceErrorText.textContent = message
			if (provinceError) provinceError.hidden = false
		}

		function clearCarriedProvinceCode() {
			try {
				sessionStorage.removeItem('speakout.province_code')
			} catch (e) {
				/* nothing to clear if storage was never reachable */
			}
		}

		// BUILD-REFERENCE section 07: a sellable province can legitimately stop
		// being sellable between visits. A held/carried code that no longer
		// matches a real option must be told to the customer in words (B11),
		// not silently dropped and re-asked with no explanation — this is
		// Figma's drawn "unavailable province" state (node 78:38341).
		//
		// Only ever called once, right after init's setActionState('add') —
		// "Add this plan" is the default selected state (78:38263 metadata:
		// step 4 "Your province" sits alongside step 3 "Add a plan" as its
		// own always-rendered step, not something an extra click reveals).
		// A prior version hid the field here whenever nothing was carried,
		// so on a fresh visit with the default plan already selected, the
		// province step silently never appeared until the user re-clicked
		// "Add this plan" — reported live on the storefront.
		function resolveProvinceUI() {
			const carried = getCarriedProvinceCode()
			if (!carried) {
				showFieldImmediately()
				return
			}
			if (findOption(carried)) {
				provinceSelect.value = carried
				showFieldImmediately()
				return
			}
			clearCarriedProvinceCode()
			showFieldImmediately()
			showProvinceError(
				provinceError && provinceError.dataset.provinceNoLongerSellableMessage
			)
		}

		function setActionState(selection) {
			const included = selection === 'add'
			if (addBtn) {
				addBtn.classList.toggle('is-selected', included)
				addBtn.setAttribute('aria-pressed', String(included))
			}
			if (skipBtn) {
				skipBtn.classList.toggle('is-selected', !included)
				skipBtn.setAttribute('aria-pressed', String(!included))
			}
			step.dataset.planIncluded = String(included)
			if (!included) hideProvinceUI()
			refreshPayToday(step)
		}

		function findPlanLine(cart) {
			return (
				cart.items.find(
					(item) => item.properties && item.properties._speakout_role === 'plan'
				) || null
			)
		}

		function checkCartForPlan() {
			return fetch('/cart.js')
				.then((response) => response.json())
				.then(findPlanLine)
				.catch(() => null)
		}

		function showSwapState() {
			if (swapEl) swapEl.hidden = false
			if (bodyEl) bodyEl.hidden = true
			hideProvinceUI()
		}

		function hideSwapState() {
			if (swapEl) swapEl.hidden = true
			if (bodyEl) bodyEl.hidden = false
		}

		// One plan per order: removes the existing plan and its paired fee
		// line together (BUILD-REFERENCE section 07 — a fee line never
		// exists without its plan), by line item key, never by variant id.
		function replaceExistingPlan() {
			return fetch('/cart.js')
				.then((response) => response.json())
				.then((cart) => {
					const planLine = findPlanLine(cart)
					if (!planLine) return
					const pairToken = planLine.properties._speakout_pair
					const updates = {}
					updates[planLine.key] = 0
					if (pairToken) {
						cart.items.forEach((item) => {
							if (item.properties && item.properties._speakout_pair === pairToken) {
								updates[item.key] = 0
							}
						})
					}
					return fetch('/cart/update.js', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						// `sections` requested per the theme's own idiom (see
						// speakout-plans-plp.js's identical pattern): a bare
						// {updates}-only body never touches the header's
						// cart-icon-bubble, so the removed plan+fee left the
						// visible count stale until a full reload — reported
						// live on the Plan PDP's "Replace plan" swap.
						body: JSON.stringify({ updates, sections: ['cart-icon-bubble'], sections_url: window.location.pathname }),
					})
				})
				.then((response) => (response ? response.json() : null))
				.then((response) => {
					publish(PUB_SUB_EVENTS.cartUpdate, { source: 'speakout-configurator' })
					const bubble = document.getElementById('cart-icon-bubble')
					if (bubble && response && response.sections && response.sections['cart-icon-bubble']) {
						const holder = document.createElement('div')
						holder.innerHTML = response.sections['cart-icon-bubble']
						const fresh = holder.querySelector('.shopify-section')
						if (fresh) bubble.innerHTML = fresh.innerHTML
					}
					hideSwapState()
					setActionState('add')
					resolveProvinceUI()
				})
		}

		if (addBtn) {
			addBtn.addEventListener('click', () => {
				checkCartForPlan().then((existing) => {
					if (existing) {
						showSwapState()
						return
					}
					setActionState('add')
					const carried = getCarriedProvinceCode()
					if (carried && findOption(carried)) {
						provinceSelect.value = carried
						showFieldImmediately()
					} else {
						showFieldAnimated(true)
					}
				})
			})
		}

		if (skipBtn) {
			skipBtn.addEventListener('click', () => setActionState('skip'))
		}

		if (provinceSelect) {
			provinceSelect.addEventListener('change', () => {
				const code = provinceSelect.value
				if (!code) return
				if (provinceError) provinceError.hidden = true
				setCarriedProvinceCode(code)
				refreshPayToday(step)
			})
		}

		if (swapConfirmBtn) {
			let swapPending = false
			swapConfirmBtn.addEventListener('click', async () => {
				if (swapPending || swapConfirmBtn.disabled || !window.SpeakoutCustomerConfirmation) return
				swapPending = true
				const confirmed = await window.SpeakoutCustomerConfirmation.confirm(swapConfirmBtn)
				swapPending = false
				if (!confirmed || !step.isConnected || swapConfirmBtn.disabled) return
				// Same loading-overlay__spinner markup/toggle the theme's own
				// product-form.js uses for its add-to-cart button (classList
				// hidden/remove-hidden) — this swap is its own real network
				// round trip (cart.js read + cart/update.js write) with no
				// other visible feedback while it's in flight.
				const spinner = swapConfirmBtn.querySelector('.loading-overlay__spinner')
				swapConfirmBtn.disabled = true
				swapConfirmBtn.classList.add('loading')
				if (spinner) spinner.classList.remove('hidden')
				replaceExistingPlan().finally(() => {
					swapConfirmBtn.disabled = false
					swapConfirmBtn.classList.remove('loading')
					if (spinner) spinner.classList.add('hidden')
				})
			})
		}

		setActionState('add')
		resolveProvinceUI()
		checkCartForPlan().then((existing) => {
			if (existing) showSwapState()
		})
	}

	// Step 5: SIM. Shares sessionStorage key speakout.byop_choice
	// ("own_sim" | "needs_sim") with the plans-page toggle in the SPKO side
	// build (theme 160739885241, speakout-plans-plp.js) — same key, same
	// two values, so a choice made on either page carries to the other.
	// Default here is "needs_sim" (SIM included), the spec's stated
	// inversion of the plans page's "own_sim" default. D6's atomic add
	// reads data-sim-included + data-sim-variant-id from this element.
	// `defaultIncluded`: device mode's spec'd default is SIM included
	// (true); plan mode's is the deliberate inversion, excluded (false) —
	// see sections/speakout-configurator.liquid's plan branch, which passes
	// false here. Only used when nothing is carried in sessionStorage yet.
	function initSimStep(root, defaultIncluded) {
		const step = (root || document).querySelector('[data-speakout-sim-step]')
		if (!step || step.dataset.speakoutSimStepInit) return
		step.dataset.speakoutSimStepInit = 'true'

		const included_by_default = defaultIncluded !== false

		const BYOP_KEY = 'speakout.byop_choice'
		const includeBtn = step.querySelector('[data-sim-action="include"]')
		const excludeBtn = step.querySelector('[data-sim-action="exclude"]')

		function getCarriedChoice() {
			try {
				return sessionStorage.getItem(BYOP_KEY)
			} catch (e) {
				return null
			}
		}

		function setCarriedChoice(value) {
			try {
				sessionStorage.setItem(BYOP_KEY, value)
			} catch (e) {
				/* sessionStorage unavailable (private mode, etc.) — the step
				   still reflects the choice for this page view */
			}
		}

		// `persist`: false only for the initial render below. Writing to
		// sessionStorage on every page load (not just on an explicit click)
		// was a live bug — visiting the phone PDP silently set 'needs_sim'
		// before the customer touched anything, which a later plan-PDP visit
		// then carried in as its default, defeating plan mode's own
		// "never defaults to adding a SIM" rule.
		function setSimState(included, persist) {
			if (includeBtn) {
				includeBtn.classList.toggle('is-selected', included)
				includeBtn.setAttribute('aria-pressed', String(included))
			}
			if (excludeBtn) {
				excludeBtn.classList.toggle('is-selected', !included)
				excludeBtn.setAttribute('aria-pressed', String(!included))
			}
			step.dataset.simIncluded = String(included)
			if (persist !== false) setCarriedChoice(included ? 'needs_sim' : 'own_sim')
			refreshPayToday(step)
		}

		if (includeBtn) {
			includeBtn.addEventListener('click', () => setSimState(true, true))
		}
		if (excludeBtn) {
			excludeBtn.addEventListener('click', () => setSimState(false, true))
		}

		// Sold-out SIM: the include button is disabled server-side (Liquid
		// `disabled` attribute); default state is excluded so a disabled
		// step never claims an unavailable line will be added.
		if (step.dataset.simAvailable === 'false') {
			setSimState(false, false)
		} else {
			const carried = getCarriedChoice()
			const included = carried === null ? included_by_default : carried !== 'own_sim'
			setSimState(included, false)
		}
	}

	// Step 6: pay-today + atomic add. Reads the state D3-D5 already own
	// rather than re-deriving any of it — this is the ONE place that reads
	// all four steps together, so it's also the one place both the visible
	// total (initPayToday) and the actual /cart/add.js call (initAtomicAdd)
	// pull from, via collectSelection. Never build the items array from two
	// different readings of "what's currently selected."
	function collectSelection(root) {
		const scope = root || document
		const variantSelects = scope.querySelector('speakout-variant-selects')
		const device = variantSelects ? variantSelects.findMatchingVariant() : null

		const planStep = scope.querySelector('[data-speakout-plan-step]')
		const planIncluded = !!planStep && planStep.dataset.planIncluded === 'true'
		let plan = null
		let fee = null
		if (planIncluded && planStep) {
			const provinceSelect = scope.querySelector(
				'[data-speakout-province-field] select'
			)
			const option =
				provinceSelect && provinceSelect.selectedOptions
					? provinceSelect.selectedOptions[0]
					: null
			plan = {
				variantId: planStep.dataset.planVariantId,
				priceCents: Number(planStep.dataset.planPriceCents || 0),
				partnerCode: planStep.dataset.planPartnerCode || '',
				billingLabel: planStep.dataset.planBillingLabel || '',
			}
			if (option && option.value) {
				fee = {
					code: option.value,
					name: option.textContent.trim(),
					variantId: option.dataset.e911VariantId,
					priceCents: Number(option.dataset.e911FeeCents || 0),
					available: option.dataset.e911Available !== 'false',
				}
			}
		}

		const simStep = scope.querySelector('[data-speakout-sim-step]')
		const simIncluded = !!simStep && simStep.dataset.simIncluded === 'true'
		const sim = simIncluded
			? {
					variantId: simStep.dataset.simVariantId,
					priceCents: Number(simStep.dataset.simPriceCents || 0),
				}
			: null

		return { device, planIncluded, plan, fee, sim }
	}

	function initPayToday(root) {
		const scope = root || document
		const container = scope.querySelector('[data-speakout-pay-today]')
		if (!container) return

		const lines = {
			device: container.querySelector('[data-pay-today-line="device"]'),
			plan: container.querySelector('[data-pay-today-line="plan"]'),
			fee: container.querySelector('[data-pay-today-line="fee"]'),
			sim: container.querySelector('[data-pay-today-line="sim"]'),
		}
		const totalEl = container.querySelector('[data-pay-today-total]')
		const feePendingText =
			lines.fee &&
			lines.fee.querySelector('[data-pay-today-amount]').textContent

		function amount(cents) {
			return typeof formatMoney === 'function'
				? formatMoney(cents)
				: '$' + (cents / 100).toFixed(2)
		}

		function render() {
			const selection = collectSelection(root)
			let totalCents = 0

			if (selection.device) {
				// Variant JSON prices are already cents (same convention the
				// theme's own variant JSON always uses — no re-scaling).
				const cents = Number(selection.device.price)
				totalCents += cents
				lines.device.querySelector('[data-pay-today-amount]').textContent =
					amount(cents)
			}

			if (lines.plan) lines.plan.hidden = !selection.plan
			if (selection.plan) {
				totalCents += selection.plan.priceCents
				lines.plan.querySelector('[data-pay-today-amount]').textContent =
					amount(selection.plan.priceCents)
			}

			if (lines.fee) lines.fee.hidden = !selection.planIncluded
			if (selection.planIncluded) {
				const feeAmountEl = lines.fee.querySelector('[data-pay-today-amount]')
				const feeDt = lines.fee.querySelector('dt')
				const provincePrefix = feeDt && feeDt.dataset.feeLabelProvincePrefix
				if (selection.fee && selection.fee.available) {
					totalCents += selection.fee.priceCents
					feeAmountEl.textContent = amount(selection.fee.priceCents)
					// Additive: only plan mode's <dt> carries this data attribute
					// (see pay-today.liquid), so device mode's label is untouched.
					if (provincePrefix) feeDt.textContent = `${provincePrefix} (${selection.fee.name})`
				} else {
					feeAmountEl.textContent = feePendingText
				}
			}

			if (lines.sim) lines.sim.hidden = !selection.sim
			if (selection.sim) {
				totalCents += selection.sim.priceCents
				lines.sim.querySelector('[data-pay-today-amount]').textContent =
					amount(selection.sim.priceCents)
			}

			if (totalEl) totalEl.textContent = amount(totalCents)
		}

		// Listen on the shared section root, not on `container` itself:
		// refreshPayToday() is dispatched from the plan/sim steps and the
		// variant-selects element, which are siblings of the pay-today
		// container in the DOM, not its descendants — a bubbled event never
		// reaches a listener on a sibling, only on a shared ancestor.
		scope.addEventListener(PAY_TODAY_REFRESH, render)
		render()
	}

	// One SIM per order (Marker SQU-17). The add below always pushes a fresh
	// SIM line when the SIM step says included, and /cart/add.js merges it
	// into any identical line already there — so adding or replacing a plan a
	// second time used to leave the cart holding 2 SIMs. replaceExistingPlan()
	// only ever swept the plan and its paired fee, never the SIM, because the
	// SIM carries no _speakout_pair token. Clear any SIM line by key first,
	// then let the add put exactly one back. Resolves to a promise either way
	// so the caller's chain is the same whether or not a sweep was needed.
	function clearExistingSims() {
		return fetch('/cart.js')
			.then((response) => response.json())
			.then((cart) => {
				const updates = {}
				cart.items.forEach((item) => {
					if (item.properties && item.properties._speakout_role === 'sim') {
						updates[item.key] = 0
					}
				})
				if (!Object.keys(updates).length) return
				return fetch('/cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ updates }),
				})
			})
			.catch(() => {
				// A failed sweep must not block the add — the cart.js
				// reconcile pass collapses any duplicate on the cart page.
			})
	}

	// Single POST /cart/add.js per BUILD-REFERENCE.md section 07: one call,
	// so a failure adds nothing (never sequential adds). Update (docs/plans/
	// imei-province-e911-plan.md S3): this call now also carries the 5 Ztar
	// cart attributes when a plan is included — imei/speakout_device_check
	// come from this same root's <device-check> element when one exists
	// (plan mode only; device mode never had a phone check to read).
	function initAtomicAdd(root) {
		const scope = root || document
		const container = scope.querySelector('[data-speakout-pay-today]')
		if (!container || container.dataset.speakoutAddInit) return
		container.dataset.speakoutAddInit = 'true'

		const addBtn = container.querySelector('[data-speakout-add-to-cart]')
		const errorEl = container.querySelector('[data-speakout-add-error]')
		const provinceField = scope.querySelector('[data-speakout-province-field]')
		const provinceSelect = provinceField ? provinceField.querySelector('select') : null
		const provinceError = scope.querySelector('[data-province-error]')
		const provinceErrorText = scope.querySelector('[data-province-error-text]')
		const planStepEl = scope.querySelector('[data-speakout-plan-step]')

		function showAddError(show, message) {
			if (!errorEl) return
			if (message) {
				const messageEl = errorEl.querySelector('.error-message')
				if (messageEl) messageEl.textContent = message
			}
			errorEl.hidden = !show
		}

		// Sold-out/unavailable plan (either mode's own plan, or the device
		// mode's best-deal upsell): disable up front rather than only
		// blocking on click, so the reason is visible before the customer
		// tries — same "disabled with a stated reason" pattern as the
		// province/fee guards below.
		if (planStepEl && planStepEl.dataset.planAvailable === 'false') {
			if (addBtn) addBtn.disabled = true
			showAddError(true, container.dataset.planUnavailableMessage || undefined)
		}

		// The province field itself may never have been revealed (plan is
		// included by default, but the field only opens on the "Add this
		// plan" click — see initPlanStep). An error inside a hidden field
		// is still hidden, so reveal the field, same as showFieldAnimated,
		// before surfacing the message.
		function showProvinceRequired() {
			if (provinceField) {
				provinceField.hidden = false
				requestAnimationFrame(() => provinceField.setAttribute('data-open', ''))
			}
			if (provinceErrorText && provinceError) {
				provinceErrorText.textContent =
					provinceError.dataset.provinceRequiredMessage || ''
			}
			if (provinceError) provinceError.hidden = false
			if (provinceSelect) provinceSelect.focus()
		}

		if (!addBtn) return

		let addPending = false
		addBtn.addEventListener('click', async () => {
			if (addPending || addBtn.disabled) return
			let selection = collectSelection(scope)
			if (selection.planIncluded) {
				if (!window.SpeakoutCustomerConfirmation) return
				addPending = true
				const confirmed = await window.SpeakoutCustomerConfirmation.confirm(addBtn)
				addPending = false
				if (!confirmed || !scope.isConnected || addBtn.disabled) return
				selection = collectSelection(scope)
			}
			showAddError(false)

			// Nothing to add: device mode always has a device; plan mode
			// (no <speakout-variant-selects> on the page, so selection.device
			// is always null there) has its plan instead. Only bail when
			// BOTH are absent — a plan-only page must still reach the payload
			// build below, which the original `!selection.device` check
			// alone silently prevented.
			if (!selection.device && !selection.planIncluded) return

			if (
				planStepEl &&
				planStepEl.dataset.planAvailable === 'false' &&
				selection.planIncluded
			) {
				return // disabled at init already states the reason
			}

			// Per BUILD-REFERENCE.md section 07 steps 2 and 6: a plan can
			// never be added without a resolved, sellable fee. Block here,
			// same as the existing province-required/no-longer-sellable
			// copy already used by the plan step.
			if (selection.planIncluded) {
				if (!selection.fee) {
					showProvinceRequired()
					return
				}
				if (!selection.fee.available) {
					if (provinceError) provinceError.hidden = false
					return
				}
			}

			const items = []
			if (selection.device) {
				items.push({
					id: Number(selection.device.id),
					quantity: 1,
					properties: { _speakout_role: 'device' },
				})
			}

			if (selection.planIncluded) {
				items.push({
					id: Number(selection.plan.variantId),
					quantity: 1,
					properties: {
						_speakout_role: 'plan',
						_speakout_pair: selection.fee.code,
						_speakout_partner_code: selection.plan.partnerCode,
						Province: selection.fee.name,
						Billing: selection.plan.billingLabel,
					},
				})
				items.push({
					id: Number(selection.fee.variantId),
					quantity: 1,
					properties: {
						_speakout_role: 'e911_fee',
						_speakout_pair: selection.fee.code,
					},
				})
			}

			if (selection.sim) {
				items.push({
					id: Number(selection.sim.variantId),
					quantity: 1,
					properties: { _speakout_role: 'sim' },
				})
			}

			const body = { items }
			if (selection.planIncluded) {
				// docs/plans/imei-province-e911-plan.md S3/S7 — final names,
				// confirmed by Ztar's Rana in writing 28 Aug 2026 (were
				// speakout_intent/speakout_province_code, both wrong). `intent`
				// is "new" for both device mode (phone+plan bundle) and plan
				// mode (standalone) until Rana answers whether a bundle order
				// needs a second value — open question on Teamwork task
				// 48573126, not blocking.
				body.attributes = {
					[CART_ATTRIBUTE_KEYS.intent]: 'new',
					[CART_ATTRIBUTE_KEYS.billingProvince]: selection.fee.code,
					[CART_ATTRIBUTE_KEYS.provinceName]: selection.fee.name,
				}

				// imei/speakout_device_check only exist for a path that ran an
				// actual phone check — plan mode's <device-check> (auto_check,
				// see device-check.liquid). Device mode's phone+plan bundle has
				// no device-check element at all (buying a new phone here never
				// required one — see this function's own top-of-file comment),
				// so both stay absent from the body rather than sent empty;
				// Ztar only ever reads these off a real BYOP plan order anyway.
				const deviceCheckEl = scope.querySelector('device-check')
				if (deviceCheckEl) {
					const imeiInput = deviceCheckEl.querySelector('input[name="imei"]')
					const imeiValue = imeiInput ? imeiInput.value.trim() : ''
					const outcome = deviceCheckEl.dataset.outcome
					if (imeiValue) body.attributes[CART_ATTRIBUTE_KEYS.imei] = imeiValue
					if (outcome) body.attributes[CART_ATTRIBUTE_KEYS.deviceCheck] = outcome
				}
			}

			// Same contract product-form.js uses (this theme's own standard
			// add-to-cart flow): request rendered `sections` so the cart
			// drawer can update itself from the response with no second
			// fetch, and publish the real PUB_SUB_EVENTS.cartUpdate so the
			// header cart icon and any other subscriber stay in sync. The
			// previous `shopify:cart:lines-update` CustomEvent had no
			// listener anywhere in the theme — cart count/drawer never
			// actually updated after an add.
			const cartDrawer = document.querySelector('cart-drawer')
			body.sections = cartDrawer ? cartDrawer.getSectionsToRender().map((s) => s.id) : undefined
			body.sections_url = window.location.pathname

			addPending = true
			addBtn.disabled = true
			// SQU-17: sweep any SIM already in the cart before adding, so the
			// add can only ever leave one behind. Skipped when this add
			// carries no SIM, which leaves an existing SIM line untouched.
			;(selection.sim ? clearExistingSims() : Promise.resolve())
				.then(() =>
					fetch('/cart/add.js', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(body),
					})
				)
				.then((response) => {
					if (!response.ok) throw new Error('add failed')
					return response.json()
				})
				.then((response) => {
					publish(PUB_SUB_EVENTS.cartUpdate, { source: 'speakout-configurator' })
					if (cartDrawer) {
						cartDrawer.renderContents(response)
					} else {
						window.location = window.routes.cart_url
					}
				})
				.catch(() => {
					showAddError(true)
				})
				.finally(() => {
					addPending = false
					addBtn.disabled = false
				})
		})
	}

	// Plan PDP D5+D6 gate (Figma node 73:28070): the add-to-cart button
	// starts disabled with "Enter your phone IMEI to continue" underneath,
	// and only enables once the phone check (device-check's own
	// speakout:device-check:outcome event) resolves to "supported" AND a
	// sellable province/fee is chosen. This is additive to, and runs
	// alongside, initAtomicAdd's own click-time validation (still the
	// thing that actually blocks/executes the POST) — this only owns the
	// button's disabled attribute and the helper line's visibility ahead
	// of any click. Device mode never required a phone check to buy a
	// device (see initAtomicAdd's own comment) — scoped to mode: 'plan'
	// only, so device mode's already-verified gate is untouched.
	function initPlanAddGate(root) {
		if (!root || root.dataset.mode !== 'plan') return
		const container = root.querySelector('[data-speakout-pay-today]')
		if (!container || container.dataset.speakoutAddGateInit) return
		container.dataset.speakoutAddGateInit = 'true'

		const addBtn = container.querySelector('[data-speakout-add-to-cart]')
		const helperEl = container.querySelector('[data-speakout-add-helper]')
		const deviceCheck = root.querySelector('device-check')
		const imeiInput = deviceCheck ? deviceCheck.querySelector('input[name="imei"]') : null
		if (!addBtn) return

		function evaluate() {
			// A sold-out/unavailable plan already disables the button with its
			// own stated reason (initAtomicAdd, above) — never override it.
			const planStep = root.querySelector('[data-speakout-plan-step]')
			if (planStep && planStep.dataset.planAvailable === 'false') return

			// docs/plans/imei-province-e911-plan.md S4 — Unknown follows the
			// settings.device_check_unknown_result admin setting (read off the
			// section root, populated in speakout-configurator.liquid).
			// Default flipped to "block" on 9 Sep 2026: with Telus' workaround
			// live, Ztar's device-check answers SUPPORTED for an approved
			// device and UNKNOWN for one that is not (Rana Haddad, in
			// writing). Unknown is a "no", so it blocks — the earlier
			// "continue" default existed only because every IMEI, including
			// Ztar's own, came back Unknown, which is no longer true.
			// not_supported and a missing/error outcome still always block.
			const outcome = deviceCheck ? deviceCheck.dataset.outcome : undefined
			const unknownResult = root.dataset.unknownResult || 'block'
			const phoneSupported =
				outcome === 'supported' || (outcome === 'unknown' && unknownResult !== 'block')
			const selection = collectSelection(root)
			const feeReady = !!(selection.planIncluded && selection.fee && selection.fee.available)
			const ready = phoneSupported && feeReady

			addBtn.disabled = !ready
			if (helperEl) {
				helperEl.hidden = ready
				if (!ready) {
					// Phone check passes first, so once it does, an unready state
					// only means the province/fee is still missing — swap the
					// copy accordingly instead of leaving the stale IMEI message.
					helperEl.textContent = phoneSupported
						? helperEl.dataset.msgProvince
						: helperEl.dataset.msgPhone
				}
			}
		}

		if (deviceCheck) {
			deviceCheck.addEventListener('speakout:device-check:outcome', evaluate)
		}
		if (imeiInput) {
			// device-check.js's own 'input' listener (bound separately, outside
			// this function) is what clears `deviceCheck.dataset.outcome` on
			// every keystroke — and listener order between two independent
			// bindings on the same event is registration order, not something
			// this function controls. Reading the dataset synchronously here
			// raced it: this gate's own evaluate() ran first, saw the still-
			// 'supported' outcome from before the edit, and left the button
			// enabled after the IMEI was cleared — reported live (emptying the
			// field kept Add to cart clickable). Deferring to a microtask runs
			// evaluate() after every same-tick 'input' listener has finished,
			// regardless of binding order, so it always sees the cleared value.
			imeiInput.addEventListener('input', () => queueMicrotask(evaluate))
		}
		root.addEventListener(PAY_TODAY_REFRESH, evaluate)
		evaluate()
	}

	// docs/plans/imei-province-e911-plan.md (v3) S6 — the listing/homepage
	// grid's "Add now" (assets/speakout-plan-grid.js) redirects here with
	// ?step=imei once a province is already known, since the province step
	// needs nothing further from the customer. URL param, not a sessionStorage
	// flag: self-clearing on the next navigation, no stale-flag failure mode.
	function initStepFocus(root) {
		if (!root || root.dataset.mode !== 'plan') return
		if (new URLSearchParams(window.location.search).get('step') !== 'imei') return
		const imeiInput = root.querySelector('device-check input[name="imei"]')
		if (imeiInput) imeiInput.focus()
	}

	// Was keyed off [data-speakout-gallery] — correct for device mode (which
	// always has a gallery) but plan mode has none, so none of the six init*
	// calls below ever ran for it (silent no-op, not just initAtomicAdd's
	// early-return). Keyed off the configurator section root instead — both
	// modes carry data-section/data-mode on the same <section> element
	// (see the section's own root markup) — so every step initializes
	// regardless of whether a gallery exists; gallery-specific init stays
	// conditional on the gallery actually being present.
	function init() {
		document.querySelectorAll('.speakout-configurator[data-section]').forEach((root) => {
			const gallery = root.querySelector('[data-speakout-gallery]')
			if (gallery) {
				initZoomImage(root)
				initGallery(gallery)
			}
			initPlanStep(root)
			initSimStep(root, root.dataset.mode !== 'plan')
			initPayToday(root)
			initAtomicAdd(root)
			initPlanAddGate(root)
			initStepFocus(root)
		})
	}

	document.addEventListener('shopify:section:load', init)
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init)
	} else {
		init()
	}
})()
